import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Trash2, Eye, EyeOff, Image as ImageIcon } from 'lucide-react';
import ImageUpload from '../../components/ImageUpload';
import { getImageUrl } from '../../utils/imageUrl';
import './AdminGallery.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminGallery = () => {
  const { token } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [formData, setFormData] = useState({
    url: '',
    alt: '',
    category: 'setups',
    order: 0,
    is_active: true
  });

  const categories = ['setups', 'romantic', 'sunset', 'events', 'food'];

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/admin/gallery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data.images);
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.post(
        `${API}/admin/gallery`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchImages();
      closeModal();
      alert('Image added successfully!');
    } catch (error) {
      console.error('Error adding image:', error);
      alert('Failed to add image');
    }
  };

  const deleteImage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${API}/admin/gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchImages();
      alert('Image deleted!');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image');
    }
  };

  const toggleActive = async (image) => {
    try {
      await axios.put(
        `${API}/admin/gallery/${image.id}`,
        { is_active: !image.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchImages();
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const openModal = () => {
    setFormData({
      url: '',
      alt: '',
      category: 'setups',
      order: 0,
      is_active: true
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const filteredImages = filterCategory === 'all'
    ? images
    : images.filter(img => img.category === filterCategory);

  if (loading) {
    return <div className="loading">Loading gallery...</div>;
  }

  return (
    <div className="admin-gallery">
      <div className="gallery-header">
        <div>
          <h2>Gallery Management</h2>
          <p>Manage your website gallery images</p>
        </div>
        <button onClick={openModal} className="btn-primary">
          <Plus size={18} /> Add Image
        </button>
      </div>

      <div className="gallery-filters">
        <button
          onClick={() => setFilterCategory('all')}
          className={`filter-btn ${filterCategory === 'all' ? 'active' : ''}`}
        >
          All Images ({images.length})
        </button>
        {categories.map((cat) => {
          const count = images.filter(img => img.category === cat).length;
          return (
            <button
              key={cat}
              onClick={() => setFilterCategory(cat)}
              className={`filter-btn ${filterCategory === cat ? 'active' : ''}`}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)} ({count})
            </button>
          );
        })}
      </div>

      <div className="gallery-grid">
        {filteredImages.map((image) => (
          <div key={image.id} className="gallery-item">
            <div className="gallery-image-wrapper">
              <img src={getImageUrl(image.url)} alt={image.alt} />
              {!image.is_active && (
                <div className="inactive-overlay">
                  <span>Inactive</span>
                </div>
              )}
              <div className="gallery-overlay-actions">
                <button
                  onClick={() => toggleActive(image)}
                  className="overlay-btn overlay-toggle"
                  title={image.is_active ? 'Deactivate' : 'Activate'}
                  data-testid={`gallery-toggle-${image.id}`}
                >
                  {image.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
                <button
                  onClick={() => deleteImage(image.id)}
                  className="overlay-btn overlay-delete"
                  title="Delete"
                  data-testid={`gallery-delete-${image.id}`}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            
            <div className="gallery-item-details">
              <div className="image-info">
                <p className="image-alt">{image.alt}</p>
                <span className="image-category">{image.category}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredImages.length === 0 && (
        <div className="empty-state">
          <ImageIcon size={48} />
          <p>No images in this category yet.</p>
          <button onClick={openModal} className="btn-primary">
            <Plus size={18} /> Add Your First Image
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Gallery Image</h3>
              <button onClick={closeModal} className="modal-close">&times;</button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Upload Image *</label>
                <ImageUpload
                  currentImageUrl={formData.url}
                  onUploadSuccess={(url) => setFormData({ ...formData, url })}
                />
                <small style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Or enter image URL manually:
                </small>
                <input
                  type="text"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://... or /api/uploads/..."
                  style={{ marginTop: '8px' }}
                />
              </div>

              <div className="form-group">
                <label>Image Description *</label>
                <input
                  type="text"
                  value={formData.alt}
                  onChange={(e) => setFormData({ ...formData, alt: e.target.value })}
                  placeholder="Describe the image..."
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span>Active (visible on website)</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={closeModal} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Add Image
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
